import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const ExportDataModal = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [exportDateRange, setExportDateRange] = useState({
    exportType: 'correspondence',
    startDate: '',
    endDate: '',
    format: 'csv'
  });

  const handleExport = async () => {
    try {
      console.log('🔍 Starting export:', exportDateRange);
      
      if (exportDateRange.exportType === 'informed_consent') {
        const token = localStorage.getItem('dentalToken');
        const params = new URLSearchParams();
        if (exportDateRange.startDate) params.append('start_date', exportDateRange.startDate);
        if (exportDateRange.endDate) params.append('end_date', exportDateRange.endDate);
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/informed-consent/export?${params.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        if (!response.ok) throw new Error('Export failed');
        
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `informed_consent_export_${Date.now()}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast({
          title: "Export Complete",
          description: "Data exported successfully",
        });
        onClose();
      } else {
        toast({
          title: "Coming Soon",
          description: "This export type is being implemented",
        });
      }
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Failed",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999999,
      }}
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          zIndex: 1000000,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Export Data</h3>
        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
          Choose what type of data to export and optionally filter by date range.
        </p>
        
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
            Export Type
          </label>
          <select
            value={exportDateRange.exportType}
            onChange={(e) => setExportDateRange(prev => ({ ...prev, exportType: e.target.value }))}
            style={{ 
              width: '100%', 
              padding: '8px 12px', 
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          >
            <option value="correspondence">Patient Post-Treatment Notes</option>
            <option value="informed_consent">Informed Consents</option>
            <option value="referrals">Referrals</option>
            <option value="med_cards">Med Cards</option>
          </select>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
            Start Date (Optional)
          </label>
          <input
            type="date"
            value={exportDateRange.startDate}
            onChange={(e) => setExportDateRange(prev => ({ ...prev, startDate: e.target.value }))}
            style={{ 
              width: '100%', 
              padding: '8px 12px', 
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>
        
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
            End Date (Optional)
          </label>
          <input
            type="date"
            value={exportDateRange.endDate}
            onChange={(e) => setExportDateRange(prev => ({ ...prev, endDate: e.target.value }))}
            style={{ 
              width: '100%', 
              padding: '8px 12px', 
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>
        
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
            Export Format
          </label>
          <select
            value={exportDateRange.format}
            onChange={(e) => setExportDateRange(prev => ({ ...prev, format: e.target.value }))}
            style={{ 
              width: '100%', 
              padding: '8px 12px', 
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          >
            <option value="csv">CSV (.csv)</option>
            <option value="excel">Excel (.xlsx)</option>
          </select>
        </div>
        
        <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
          <Button
            onClick={handleExport}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
          >
            Export {exportDateRange.format.toUpperCase()}
          </Button>
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

export default ExportDataModal;
